import _ from 'lodash'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'

import { Box, Container, Divider, Paper, Stack, Typography } from '@mui/material'

import { useCreateWaitingRoomMutation } from '@/core/services/waiting-room'
import { useGetNetworkQuery } from '@/core/services/network'
import { WaitingRoomForm, WaitingRoomFormData } from '@/features/waiting-room'
import NotFoundPage from '@/pages/error/NotFoundPage'

import { getApiErrorMessage } from '@/utils/errors'
import { useNetworkMemberPermissions } from '../hooks/useNetworkMemberPermissions'

export default function NetworkCreateWaitingRoom() {
  const navigate = useNavigate()
  const { networkId } = useParams<{ networkId: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: network } = useGetNetworkQuery(networkId ?? skipToken)
  const { isNetworkAdmin } = useNetworkMemberPermissions({ networkId })

  const [createWaitingRoom, { isLoading: isCreating }] = useCreateWaitingRoomMutation()

  const initialValues = useMemo(
    () => ({
      avgWaitingTime: 15,
      description: '',
      doctors: [],
      establishments: [],
      isPrioritized: false,
      name: '',
      openingHours: _.range(7).map((day) => ({
        day,
        periods: [],
      })),
      specialities: [],
      price: 0,
      paymentReceiverId: undefined,
      allowPatientSelection: false,
      paymentDialogConfig: { enabled: false, showForRoles: [] },
      fairDistribution: { enabled: false, maxConcurrent: 1, cooldownSeconds: 60, strategy: 'cap' as const, offerTtlSeconds: 30, prominentOffer: false },
      readinessConfig: { enabled: false, prioritizeReady: false, checklistTemplateId: '' },
      coPresenceConfig: { enabled: false, allowAssign: false },
    }),
    []
  )

  const handleSubmit = async (values: WaitingRoomFormData) => {
    if (!networkId) return
    if (values.paymentReceiverId === '') values.paymentReceiverId = undefined
    if (values.readinessConfig.checklistTemplateId === '') values.readinessConfig.checklistTemplateId = undefined
    try {
      await createWaitingRoom({
        ...values,
        network: networkId,
        specialities: _.map(values.specialities, (speciality) => _.pick(speciality, ['code', 'name', 'selectByDefault'])),
      }).unwrap()
      enqueueSnackbar(t('notification.waitingRoom.create.success'), { variant: 'success' })
      navigate(`/networks/${networkId}/waiting-rooms`)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.create.error')), { variant: 'error' })
    }
  }

  // Redirect if not an admin
  if (!network || !isNetworkAdmin) {
    return (
      <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
        <NotFoundPage />
      </Stack>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper>
        <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
              {t('page.network.createWaitingRoom.label')}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Box p={3}>
          <WaitingRoomForm initialValues={initialValues} isSubmitting={isCreating} onSubmit={handleSubmit} />
        </Box>
      </Paper>
    </Container>
  )
}
